<template>
  <v-container>
    <v-row>
      <v-col>
        <DragAndDropFile
            ref="dragAndDrop"
            custom-height="80vh"
            not-required
            :view-only="!isNewItem"
            @fileChange="setFile($event)"
        />
      </v-col>
      <v-col>
        <v-form ref="form">
          <v-row justify="space-between">
            <v-col cols="12" md="4">
              <CustomerTypeAhead
                ref="customerTypeahead"
                :value="taskDocumentation.supplierId"
                @input="taskDocumentation.supplierId = $event"
                :label="$t('book/issue:common.supplier')"
                required
                :suppliersOnly="true"
              />
              </v-col>
              <v-col cols="12" md="4">
                <DateField
                  ref="validFrom"
                  @update="(v) => taskDocumentation.startDate = v"
                  :isClearable="true"
                  :key="validFromKey"
                :readonly="viewOnly"
                :label="$t('common:valid-from')"
              />
            </v-col>
            <v-col cols="12" md="4">
                <v-tooltip top>
                  <template v-slot:activator="{on}">
                     <span v-on="on">
                       <v-checkbox :label="$t('book/issue:common.serves-fault-report')" v-model="taskDocumentation.faultReport" />
                     </span>
                  </template>
                  <span>{{$t('book/issue:common.serves-fault-report-detailed-description')}}</span>
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
              <v-textarea :readonly="viewOnly" clearable outlined :label="$t('common:note')" v-model="taskDocumentation.note" />
              </v-col>
            </v-row>
            <v-row v-if="!hideButton">
              <v-col>
                <BaseButton
                  color="primary"
                  :btnText="buttonLabel"
                  @clickedButton="save()"
                />

                <TwoButtonDialog
                  ref="deleteButton"
                  v-if="!isNewItem"
                  :btn-text="$t('buttons:delete')"
                  color="red"
                  :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/issue:taskDocumentation')})"
                  :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/issue:taskDocumentation')})"
                  :button1-text="$t('buttons:cancel')"
                  :button2-text="$t('buttons:delete')"
                  @clickedButton1="$refs.deleteButton.showDialog = false"
                  @clickedButton2="removeDocument"
                  hide-overlay
                />
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
  </template>
  <script>

  import TaskDocumentationService from '@/modules/book/services/task-documentation/task-documentation-service';
  import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog';
  import DocumentationService from '@/modules/core/services/documentation-service';
  import CustomerTypeAhead from '@/modules/core/components/CustomerTypeAhead.vue';
  import DragAndDropFile from '@/modules/core/components/DragAndDropFile';
  import TaskDocumentation from '@/modules/book/data/task-documentation';
  import BaseButton from '@/modules/core/components/Base/BaseButton';
  import {DocumentType} from '@/modules/book/data/document-type';
  import DateField from '@/modules/core/components/DateField';
  import {ruleRequired} from '@/modules/core/services/rules';
  import Utils from '@/modules/core/services/utils';

  export default {
    name: 'TaskDocumentationForm',
    components: {CustomerTypeAhead, BaseButton, DragAndDropFile, DateField, TwoButtonDialog},
    props: {
    hideButton: Boolean,
    viewOnly: Boolean
    },
    data() {
      return {
        file: null,
        validFromKey: 0,
        taskDocumentation: new TaskDocumentation(),
        isNewItem: false
      }
    },
    computed: {
      buttonLabel() {
      if (!this.isNewItem) return this.$t('buttons:save-changes');

      return this.$t('buttons:add-specific', { specific: this.$t('book/issue:taskDocumentation') });
    }
  },
  methods: {
    ruleRequired,
    setInputData(inputData) {
      this.taskDocumentation.task = inputData.taskId;
      this.taskDocumentation.customer = inputData.customerId;
      this.$refs.dragAndDrop.uploadAsUrl(inputData.file);
      this.file = inputData.file;
    },
    setCreateData(taskInfo) {
      this.taskDocumentation.task = taskInfo.id;
      this.taskDocumentation.customer = taskInfo.contact;
      this.isNewItem = true;
    },
    setEditData(document) {
      if (!document) return;
      this.taskDocumentation = document;

      if (this.taskDocumentation.startDate) {
        this.taskDocumentation.startDate =
            this.$refs.validFrom.parseDTO(this.taskDocumentation.startDate);
      }

      if (this.taskDocumentation.supplierId !== null)
        this.$refs.customerTypeahead.initialise(this.taskDocumentation.supplierId);

        this.loadStaticFileUrl();
      },
    save() {
      this.$refs.dragAndDrop.isMandatoryFilledOut();
      if (!this.$refs.form.validate()
          || !this.$refs.dragAndDrop.isMandatoryFilledOut()) {
        document.getElementsByClassName("v-dialog--active")[0].scrollTop = 0;
        return;
      }

      let label = this.taskDocumentation.id ? 'snackbar:update-success' : 'snackbar:add-success';
      let failLabel = this.taskDocumentation.id ? 'snackbar:update-failure' : 'snackbar:add-failure';
      TaskDocumentationService.saveOrUpdate(
          this.taskDocumentation,
          this.file,
          () => {
            Utils.showSuccessSnackbar(this.$t(label, {name: this.$t('book/issue:offerDocumentation')}));
            this.$emit('closeDialog');
          },
          () => {
            Utils.showFailureSnackbar(this.$t(failLabel, {name: this.$t('book/issue:offerDocumentation')}))
            this.$emit('closeDialog');
          }
      )
    },
    setFile(item) {
      this.file = item;

      if (item) {
        let docuToParse = {
          fileType: item.type,
          src: URL.createObjectURL(item)
        }
        this.$refs.dragAndDrop.setPreviewSrc(docuToParse)
      } else {
        this.$refs.dragAndDrop.setPreviewSrc(null)
      }

      this.$refs.dragAndDrop.file = item;
      this.$refs.dragAndDrop.isUploading = false;
    },
    clearForm() {
      this.documentation = new TaskDocumentation();
      this.$refs.dragAndDrop.clear();
      this.$refs.form.reset();
      this.validFromKey++;
      this.isNewItem = false;
    },
    loadStaticFileUrl() {
      DocumentationService.getDocByTypeAndId(
          this.taskDocumentation.id,
          this.taskDocumentation.fileType,
          DocumentType.TASK,
          this.taskDocumentation.task,
          (fileUrl) => {
            this.taskDocumentation.src = fileUrl;
            this.$refs.dragAndDrop.setPreviewSrc(this.taskDocumentation);
          }
      )
    },
    removeDocument() {
      TaskDocumentationService.delete(this.taskDocumentation.id,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('book/issue:taskDocumentation')}));
            this.$emit('closeDialog');
          },
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('book/issue:taskDocumentation')}));
            this.$emit('closeDialog');
          }
      );
    }
  }
}
</script>